import React from 'react';
import PropTypes from 'prop-types';
import { parseISO, format, differenceInMinutes } from 'date-fns';
import styled from 'styled-components';
import { Avatar } from '@companycam/slab-web';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAvatarData } from 'javascripts/hooks';
import { LD_WORKFLOWS_REST_PROJECT_TASKS } from 'constants/featureFlags';
import CommentContainer from 'components/activity/CommentContainer';
import TagContainer from 'components/activity/TagContainer';
import ProjectDetails from './ProjectDetails';
import ProjectTasks from './ProjectTasks';
import LegacyProjectTasks from './LegacyProjectTasks';
import AssetDescription from './descriptions/AssetDescription';

const ContributorAvatar = styled.div`
  margin-right: 12px;
`;

const ItemMetaContainer = ({
  asset,
  isHidden,
  onRequestClose,
  displayCompanyName,
  setKeyboardNavigationEnabled,
}) => {
  const avatarData = useAvatarData(asset);
  const flags = useFlags();
  const restProjectTasksEnabled = flags[LD_WORKFLOWS_REST_PROJECT_TASKS];

  if (!asset) {
    return null;
  }

  let containerClasses = 'asset-meta-container';
  if (isHidden) {
    containerClasses += ' hide-meta-container';
  }

  const uploadDate = parseISO(asset.capturedAt);
  const receivedDate = parseISO(asset.createdAt);

  const isSignificantlyDifferent =
    differenceInMinutes(receivedDate, uploadDate) >= 5;

  const photoContributor = (
    <>
      {asset.contributor.name}
      {displayCompanyName ? (
        <span className="photo-by-user-account">
          &nbsp;
          {`(${asset.contributor.companyName})`}
        </span>
      ) : null}
    </>
  );

  let projectTasksComponent = null;

  const formattedUploadDate = format(
    new Date(parseISO(asset.capturedAt)),
    'M/d/yyyy, h:mm a',
  );

  const formattedReceivedDate = format(new Date(), 'M/d/yyyy, h:mm a');

  // REST project tasks
  if (restProjectTasksEnabled) {
    projectTasksComponent = (
      <ProjectTasks
        projectInfo={{
          ...asset.projectInfo,
          nodeId: asset.projectInfo?.id,
          databaseId: Number(asset.projectInfo?.databaseId),
        }}
        assetDbId={Number(asset.databaseId)}
        assetType={asset.__typename}
        isGalleryView
      />
    );
  }
  // graphql project tasks
  else {
    projectTasksComponent = (
      <LegacyProjectTasks
        assetId={asset.id}
        assetType={asset.__typename}
        projectId={asset.projectInfo.id}
      />
    );
  }

  return (
    <div
      className={containerClasses}
      data-testid="gallery__asset-meta-container"
    >
      <div className="asset-meta-header">
        <div
          className="modal-close-button"
          onClick={onRequestClose}
          data-testid="asset__close-button"
        >
          <i className="mdi mdi-close" />
        </div>
        <ProjectDetails projectInfo={asset.projectInfo} />
        <div className="single-asset-meta">
          <span className="asset-created-meta">
            <ContributorAvatar>
              <Avatar
                fullName={asset.contributor.name}
                size="medium"
                imageSrc={asset.contributor.avatar}
                borderColor={
                  avatarData.isExternal ? avatarData.color : undefined
                }
              />
            </ContributorAvatar>
            <span className="photo-by-wrap">
              {asset.contributor.webURL ? (
                <a
                  href={asset.contributor.webURL}
                  className="photo-by-meta"
                  data-testid="asset__contributor"
                >
                  {photoContributor}
                </a>
              ) : (
                <span className="photo-by-meta--no-link">
                  {photoContributor}
                </span>
              )}

              <span
                className="photo-taken-meta"
                data-testid="asset__upload-date"
              >
                {formattedUploadDate}
              </span>
              {isSignificantlyDifferent ? (
                <span className="photo-taken-meta">
                  Received:&nbsp;
                  {formattedReceivedDate}
                </span>
              ) : null}
            </span>
          </span>

          <span className="asset-tags-meta">
            <TagContainer
              nodeId={asset.id}
              recordableType={asset.__typename.toLowerCase()}
            />
          </span>
        </div>
      </div>

      {projectTasksComponent}

      <AssetDescription
        assetNodeId={asset.id}
        setKeyboardNavigationEnabled={setKeyboardNavigationEnabled}
      />
      <CommentContainer
        nodeId={asset.id}
        projectId={asset.projectInfo.id}
        recordableType={asset.__typename.toLowerCase()}
      />
    </div>
  );
};

ItemMetaContainer.propTypes = {
  asset: PropTypes.shape({
    __typename: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    databaseId: PropTypes.string.isRequired,
    capturedAt: PropTypes.string,
    createdAt: PropTypes.string,
    contributor: PropTypes.shape({
      webURL: PropTypes.string,
      name: PropTypes.string.isRequired,
      companyName: PropTypes.string.isRequired,
      avatar: PropTypes.string,
    }).isRequired,
    projectInfo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      databaseId: PropTypes.string.isRequired,
      formattedLine1: PropTypes.string.isRequired,
      formattedLine2: PropTypes.string.isRequired,
    }).isRequired,
  }),
  isHidden: PropTypes.bool,
  onRequestClose: PropTypes.func,
  displayCompanyName: PropTypes.bool,
  setKeyboardNavigationEnabled: PropTypes.func,
};

ItemMetaContainer.defaultProps = {
  setKeyboardNavigationEnabled: () => {},
};

export default ItemMetaContainer;
